import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, ChevronRightIcon, ChevronLeftIcon, CalendarIcon, ArrowRightIcon, FilterIcon, StarIcon, BookOpenIcon, SparklesIcon, BookIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { usePlatform } from '../context/PlatformContext';
import { useAfterTideFilter } from '../hooks/useAfterTideFilter';
import { allBooks } from '../data/booksData';
import { aftertideBooks } from '../data/aftertideBooksData';
import UnifiedBookCard from '../components/Books/UnifiedBookCard';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
// Mock data for new releases
const newReleases = [{
  id: '1',
  title: 'The Midnight Garden',
  author: 'Elena Richards',
  coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.7,
  reviewCount: 42,
  genres: ['Fantasy', 'Romance'],
  releaseDate: '2023-09-01',
  description: 'A magical romance that blossoms in a garden that only appears at midnight.',
  spiceLevel: 3,
  darknessLevel: 2,
  relationshipType: 'Enemies to Lovers',
  formats: ['hardcover', 'ebook', 'audiobook'],
  isNew: true
}, {
  id: '2',
  title: 'Whispers in the Dark',
  author: 'Marcus Chen',
  coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.5,
  reviewCount: 28,
  genres: ['Horror', 'Thriller'],
  releaseDate: '2023-09-01',
  description: 'A chilling tale of supernatural encounters that will keep you up at night.',
  spiceLevel: 1,
  darknessLevel: 5,
  formats: ['paperback', 'ebook'],
  isNew: true
}, {
  id: '3',
  title: 'The Last Detective',
  author: 'Sophia Williams',
  coverImage: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.8,
  reviewCount: 36,
  genres: ['Mystery', 'Crime'],
  releaseDate: '2023-08-28',
  description: 'In a world where AI has replaced human detectives, one investigator refuses to be obsolete.',
  spiceLevel: 2,
  darknessLevel: 4,
  formats: ['hardcover', 'ebook', 'audiobook'],
  isNew: true
}, {
  id: '4',
  title: 'Beyond the Stars',
  author: 'David Miller',
  coverImage: 'https://images.unsplash.com/photo-1531928351158-2f736078e0a1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.6,
  reviewCount: 31,
  genres: ['Science Fiction', 'Adventure'],
  releaseDate: '2023-08-28',
  description: 'An epic space adventure that challenges our understanding of humanity and the universe.',
  spiceLevel: 2,
  darknessLevel: 3,
  formats: ['paperback', 'ebook', 'audiobook'],
  isNew: true
}, {
  id: '5',
  title: 'The Silent Echo',
  author: 'Emma Johnson',
  coverImage: 'https://images.unsplash.com/photo-1589998059171-988d887df646?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.9,
  reviewCount: 45,
  genres: ['Literary Fiction', 'Drama'],
  releaseDate: '2023-08-25',
  description: 'A moving exploration of silence, communication, and the human connection.',
  spiceLevel: 1,
  darknessLevel: 3,
  formats: ['paperback', 'ebook'],
  isNew: true
}, {
  id: '6',
  title: 'Quantum Dreams',
  author: 'Alex Zhang',
  coverImage: 'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.4,
  reviewCount: 27,
  genres: ['Science Fiction', 'Thriller'],
  releaseDate: '2023-08-22',
  description: 'A mind-bending journey through quantum realities where dreams and physics collide.',
  spiceLevel: 2,
  darknessLevel: 3,
  relationshipType: 'Slow Burn',
  formats: ['hardcover', 'ebook', 'audiobook'],
  isNew: true
}, {
  id: '7',
  title: 'Hearts Entwined',
  author: 'Sarah Blake',
  coverImage: 'https://images.unsplash.com/photo-1603162525937-92a06da4b8ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.3,
  reviewCount: 19,
  genres: ['Romance', 'Contemporary'],
  releaseDate: '2023-08-22',
  description: 'Two hearts find each other in the most unexpected of places.',
  spiceLevel: 4,
  darknessLevel: 1,
  relationshipType: 'Friends to Lovers',
  formats: ['ebook', 'audiobook'],
  isNew: true
}, {
  id: '8',
  title: 'The Forgotten Kingdom',
  author: 'Michael Torres',
  coverImage: 'https://images.unsplash.com/photo-1614332287897-cdc485fa562d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.6,
  reviewCount: 33,
  genres: ['Fantasy', 'Adventure'],
  releaseDate: '2023-08-18',
  description: 'An ancient kingdom rises from the ashes, and with it, old enemies.',
  spiceLevel: 2,
  darknessLevel: 4,
  formats: ['hardcover', 'paperback', 'ebook'],
  isNew: true
}];
// Helper: group books by release date
const groupBooksByDate = (books: typeof newReleases) => {
  const groups: Record<string, typeof newReleases> = {};
  books.forEach(book => {
    const date = book.releaseDate;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(book);
  });
  // Sort by date descending (newest first)
  const sortedEntries = Object.entries(groups).sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime());
  return sortedEntries;
};
// Helper: format date for display
const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const today = new Date();
  const diffDays = Math.floor((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });
};
// Helper: get genre color
const getGenreColor = (genre: string): string => {
  const g = genre.toLowerCase();
  if (g.includes('romance')) return 'bg-pink-600';
  if (g.includes('fantasy')) return 'bg-indigo-600';
  if (g.includes('sci-fi') || g.includes('science')) return 'bg-blue-600';
  if (g.includes('mystery')) return 'bg-indigo-600';
  if (g.includes('thriller')) return 'bg-red-600';
  if (g.includes('horror')) return 'bg-gray-800';
  if (g.includes('historical')) return 'bg-amber-600';
  if (g.includes('literary')) return 'bg-emerald-600';
  if (g.includes('crime')) return 'bg-slate-600';
  if (g.includes('adventure')) return 'bg-orange-600';
  if (g.includes('contemporary')) return 'bg-teal-600';
  return 'bg-violet-600';
};
// Carousel component for a single date's books
function DateCarousel({
  books,
  darkMode
}: {
  books: typeof newReleases;
  darkMode: boolean;
}) {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(books.length > 4);
  const checkScrollButtons = () => {
    if (carouselRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  const scroll = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const scrollAmount = carouselRef.current.clientWidth * 0.8;
      const newScrollPosition = carouselRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      carouselRef.current.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
      setTimeout(checkScrollButtons, 300);
    }
  };
  return <div className="relative group" data-id="element-12130" data-mp="19oat5a7yd5h7">
      {/* Left scroll button */}
      {canScrollLeft && <button onClick={() => scroll('left')} className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-opacity ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-800 hover:bg-gray-100'}`} data-id="element-12131" data-mp="14p7mjlcbqr74">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-12132" data-mp="1sfbto2oq192v" />
        </button>}

      {/* Carousel */}
      <div ref={carouselRef} className="flex space-x-4 overflow-x-auto pb-4 scrollbar-hide" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} onScroll={checkScrollButtons} data-id="element-12133" data-mp="1nf7a23siny8f">
        {books.map(book => <div key={book.id} className="flex-shrink-0 w-[140px] sm:w-[160px] md:w-[180px]" data-id="element-12134" data-mp="2b51k6dlgb8oz">
            <UnifiedBookCard book={book} darkMode={darkMode} data-id="element-12135" data-mp="26dih0ow1nqoa" />
          </div>)}
      </div>

      {/* Right scroll button */}
      {canScrollRight && <button onClick={() => scroll('right')} className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-opacity ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-800 hover:bg-gray-100'}`} data-id="element-12136" data-mp="3u0ehi73pypvr">
          <ChevronRightIcon className="h-5 w-5" data-id="element-12137" data-mp="bvkza13i44wt" />
        </button>}
    </div>;
}
export default function NewBooks() {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const {
    platformData
  } = useAfterTideFilter();
  // Use platform-appropriate data
  const baseBooks = platformData(allBooks, aftertideBooks);
  const [timeframe, setTimeframe] = useState<'this-week' | 'this-month' | 'coming-soon'>('this-week');
  const [searchTerm, setSearchTerm] = useState('');
  // Sidebar filter state
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [selectedSpice, setSelectedSpice] = useState('');
  const activeFilterCount = (timeframe !== 'all' ? 1 : 0) + selectedGenres.length + selectedFormats.length + (selectedSpice ? 1 : 0);
  const filterSections: FilterSection[] = [{
    id: 'timeframe',
    title: 'Timeframe',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Recent'
    }, {
      value: 'today',
      label: 'Today'
    }, {
      value: 'this-week',
      label: 'This Week'
    }, {
      value: 'this-month',
      label: 'This Month'
    }],
    value: timeframe,
    onChange: setTimeframe
  }, {
    id: 'genre',
    title: 'Genre',
    type: 'checkbox',
    options: [{
      value: 'fantasy',
      label: 'Fantasy'
    }, {
      value: 'romance',
      label: 'Romance'
    }, {
      value: 'mystery',
      label: 'Mystery'
    }, {
      value: 'thriller',
      label: 'Thriller'
    }, {
      value: 'sci-fi',
      label: 'Sci-Fi'
    }, {
      value: 'horror',
      label: 'Horror'
    }, {
      value: 'literary',
      label: 'Literary Fiction'
    }, {
      value: 'contemporary',
      label: 'Contemporary'
    }],
    value: selectedGenres,
    onChange: (val: string) => setSelectedGenres(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'format',
    title: 'Format',
    type: 'checkbox',
    options: [{
      value: 'ebook',
      label: 'eBook'
    }, {
      value: 'paperback',
      label: 'Paperback'
    }, {
      value: 'hardcover',
      label: 'Hardcover'
    }, {
      value: 'audiobook',
      label: 'Audiobook'
    }],
    value: selectedFormats,
    onChange: (val: string) => setSelectedFormats(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'spice',
    title: 'Spice Level',
    type: 'radio',
    options: [{
      value: '',
      label: 'Any'
    }, {
      value: '1',
      label: '🌶 Mild (1)'
    }, {
      value: '2',
      label: '🌶🌶 Warm (2)'
    }, {
      value: '3',
      label: '🌶🌶🌶 Hot (3)'
    }, {
      value: '4',
      label: '🌶🌶🌶🌶 Steamy (4)'
    }, {
      value: '5',
      label: '🌶🌶🌶🌶🌶 Scorching (5)'
    }],
    value: selectedSpice,
    onChange: setSelectedSpice
  }];
  const resetFilters = () => {
    setTimeframe('all');
    setSelectedGenres([]);
    setSelectedFormats([]);
    setSelectedSpice('');
    setSearchTerm('');
  };
  // Filter books
  const filteredBooks = baseBooks.filter(book => {
    if (selectedGenres.length > 0 && !book.genres.some(g => selectedGenres.some(sg => g.toLowerCase().includes(sg.toLowerCase())))) return false;
    if (selectedFormats.length > 0 && !book.formats.some(f => selectedFormats.includes(f))) return false;
    if (selectedSpice && book.spiceLevel !== parseInt(selectedSpice)) return false;
    if (searchTerm && !book.title.toLowerCase().includes(searchTerm.toLowerCase()) && !book.author.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });
  const booksByDate = groupBooksByDate(filteredBooks);
  const featuredBook = baseBooks.reduce((best, book) => book.rating > best.rating ? book : best);
  return <div className={`min-h-screen ${dk.page}`} data-id="element-12138" data-mp="2szwtrltkzwu8">
      {/* Header */}
      <div className={`py-6 px-4 ${dk.surface}`} data-id="element-12139" data-mp="2xu132bqbkgyh">
        <div className="max-w-7xl mx-auto" data-id="element-12140" data-mp="31ubi0dw3ahph">
          <div className={`flex items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-12141" data-mp="31t1kpqx29djw">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-12142" data-mp="254u6vjec83ge">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-12143" data-mp="2a501n8fwm62p" />
            <Link to="/browse" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-12144" data-mp="2e3z4mni2rsri">
              Books
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-12145" data-mp="3pkfb3rbniz28" />
            <span className={dk.textPrimary} data-id="element-12146" data-mp="3pm06tk9pco1o">New Releases</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-12147" data-mp="3uhfic5k8m7ux">
            <div className="flex items-center gap-3" data-id="element-12148" data-mp="8g87696jh10l">
              <CalendarIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-12149" data-mp="3iq5n3uupo2m" />
              <div data-id="element-12150" data-mp="3kjcaj5gs8am">
                <h1 className={`text-2xl md:text-3xl font-bold ${dk.textHeading}`} data-id="element-12151" data-mp="3ux49cznydks1">
                  New Releases
                </h1>
                <p className={`mt-1 text-sm ${dk.textSecondary}`} data-id="element-12152" data-mp="3qvaex94cpd0b">
                  Discover the latest books hitting shelves this month.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto" data-id="element-12153" data-mp="3gw1mwlt1q8gl">
              <div className="relative flex-grow md:w-80" data-id="element-12154" data-mp="3bzb59u1lf4t2">
                <input type="text" placeholder="Search new releases..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full px-5 py-2.5 pl-10 rounded-lg border focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} data-id="element-12155" data-mp="37wod1s10fhvc" />
                <SearchIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${dk.textMuted}`} data-id="element-12156" data-mp="2zmpmuz0w57wd" />
              </div>
              <Link to="/browse?sort=newest" className={`flex items-center px-4 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap ${dk.card} ${dk.textPrimary} border ${dk.border}`} data-id="element-12157" data-mp="2qodlc1yhkpp5">
                Browse All
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-12158" data-mp="39en16en74fzb">
        {/* Mobile Filter Button */}
        <div className="mb-6 lg:hidden" data-id="element-12159" data-mp="3ebvjcf8cgk0k">
          <button onClick={() => setMobileFiltersOpen(true)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border shadow-sm ${dk.card} ${dk.textSecondary}`} data-id="element-12160" data-mp="3ef14xkhrl3rj">
            <FilterIcon className="h-4 w-4" data-id="element-12161" data-mp="22vezm9prbh7w" />
            Filters
            {activeFilterCount > 0 && <span className={`ml-1 px-1.5 py-0.5 ${rt.badgeRadius} text-xs font-bold text-white ${rt.bgDark}`} data-id="element-12162" data-mp="1qf5vh1puq271">
                {activeFilterCount}
              </span>}
          </button>
        </div>

        <div className="flex gap-8" data-id="element-12163" data-mp="1zh5c3swyf49g">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-12164" data-mp="1bpzv3afa25xk">
            <FilterSidebar sections={filterSections} onReset={resetFilters} data-id="element-12165" data-mp="1lhdrvafugrn0" />
          </FilterSidebarWrapper>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-10" data-id="element-12166" data-mp="szi8zooya8l6">
            {/* Featured Spotlight */}
            <section className={`rounded-xl overflow-hidden border ${dk.border} ${dk.card}`} data-id="element-12167" data-mp="o0ovvxdksjlj">
              <div className="flex flex-col md:flex-row md:max-h-48" data-id="element-12168" data-mp="2i5grqza8rtd">
                <div className="w-full md:w-1/3 relative" data-id="element-12169" data-mp="7ggre3xej6cn">
                  <img src={featuredBook.coverImage} alt={featuredBook.title} className="w-full h-48 md:h-full object-cover" data-id="element-12170" data-mp="7gyt3g2u2cry" />
                  {featuredBook.genres[0] && <span className={`absolute top-3 left-3 text-white text-xs font-medium px-2 py-0.5 rounded-full ${getGenreColor(featuredBook.genres[0])}`} data-id="element-12171" data-mp="lgrhv5usuixa">
                      {featuredBook.genres[0]}
                    </span>}
                  <span className="absolute top-3 right-3 bg-indigo-600 text-white text-xs font-medium px-2 py-0.5 rounded-full" data-id="element-12172" data-mp="104uomhgo1yn5">
                    NEW
                  </span>
                  <div className="absolute bottom-2 left-2 md:hidden flex items-center gap-1 bg-black/60 rounded-full px-2 py-0.5" data-id="element-12173" data-mp="152e2hg9gc9ek">
                    <StarIcon className="h-3 w-3 text-amber-400 fill-amber-400" data-id="element-12174" data-mp="2to3e4z02o8al" />
                    <span className="text-white text-xs font-semibold" data-id="element-12175" data-mp="2pmhrilxqkfn7">
                      {featuredBook.rating}
                    </span>
                    <span className="text-white/70 text-xs" data-id="element-12176" data-mp="2kny8ouefvjz2">
                      ({featuredBook.reviewCount})
                    </span>
                  </div>
                </div>
                <div className={`w-full md:w-2/3 p-4 flex flex-col justify-between ${dk.card}`} data-id="element-12177" data-mp="3dasmx9knxn43">
                  <div data-id="element-12178" data-mp="38dizu0kqxnjf">
                    <div className="flex items-center justify-between gap-2 mb-1" data-id="element-12179" data-mp="33m11lka3y125">
                      <div className="flex items-center gap-2" data-id="element-12180" data-mp="33jxzi1vokhh2">
                        <SparklesIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-12181" data-mp="2ym4uvda0zviw" />
                        <span className={`text-xs font-semibold uppercase tracking-wide ${rt.textPrimary}`} data-id="element-12182" data-mp="3w2lfj2vtl6ba">
                          Just Released
                        </span>
                      </div>
                      <span className={`text-xs ${dk.textMuted}`} data-id="element-12183" data-mp="cr39mmubaf7t">
                        {formatDate(featuredBook.releaseDate)}
                      </span>
                    </div>
                    <h2 className={`text-lg font-bold mb-0.5 ${dk.textHeading}`} data-id="element-12184" data-mp="2vjnut9gowws">
                      {featuredBook.title}
                    </h2>
                    <p className={`text-xs mb-2 ${dk.textSecondary}`} data-id="element-12185" data-mp="vj5sm4ufe7l8">
                      by {featuredBook.author}
                    </p>
                    <p className={`text-xs mb-2 line-clamp-2 ${dk.textSecondary}`} data-id="element-12186" data-mp="ltw38wqyv30t">
                      {featuredBook.description}
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-3" data-id="element-12187" data-mp="1eaoyqkjqy1rh">
                    <div className="flex items-center gap-1" data-id="element-12188" data-mp="19jypgw879eri">
                      <StarIcon className="h-4 w-4 text-amber-400 fill-amber-400" data-id="element-12189" data-mp="19hlzbvsafy3d" />
                      <span className={`text-sm font-semibold ${dk.textPrimary}`} data-id="element-12190" data-mp="2s81baiouqgx8">
                        {featuredBook.rating}
                      </span>
                      <span className={`text-xs hidden md:inline ${dk.textMuted}`} data-id="element-12191" data-mp="315tq7lqnvmk3">
                        ({featuredBook.reviewCount} reviews)
                      </span>
                    </div>
                    <Link to={`/book/${featuredBook.id}`} className={`flex items-center justify-center px-4 py-1.5 rounded-lg font-medium text-sm text-white transition-colors ${rt.primaryButton}`} data-id="element-12192" data-mp="3g45lbnydyn95">
                      View Book <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-12193" data-mp="3oyjzfn76jrqo" />
                    </Link>
                  </div>
                </div>
              </div>
            </section>

            {/* Date-grouped carousels */}
            {booksByDate.map(([date, books]) => <section key={date} data-id="element-12194" data-mp="1yt6z48lvqhdv">
                <div className="flex items-center justify-between mb-4" data-id="element-12195" data-mp="1ttkpe959fj9t">
                  <div className="flex items-center gap-2" data-id="element-12196" data-mp="1tvdtphsehren">
                    <CalendarIcon className={`h-5 w-5 ${rt.textPrimary}`} data-id="element-12197" data-mp="28jihv1a82v4t" />
                    <div data-id="element-12198" data-mp="23s0fc8sjwmhq">
                      <h2 className={`text-lg font-bold ${dk.textHeading}`} data-id="element-12199" data-mp="23qpbya30pgr5">
                        {formatDate(date)}
                      </h2>
                      <p className={`text-xs ${dk.textMuted}`} data-id="element-12200" data-mp="2hkzug9of8eor">
                        {books.length} book{books.length > 1 ? 's' : ''}{' '}
                        released
                      </p>
                    </div>
                  </div>
                </div>
                <DateCarousel books={books} darkMode={isAfterTide} data-id="element-12201" data-mp="2mirk0x1lfops" />
              </section>)}

            {filteredBooks.length === 0 && <div className="text-center py-12" data-id="element-12202" data-mp="s4gz2zo4depa">
                <BookIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-12203" data-mp="s61qwhj1ujjg" />
                <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-12204" data-mp="33tnd7b0i6ut7">
                  No new releases found
                </h3>
                <p className={dk.textMuted} data-id="element-12205" data-mp="2u2ao2r63aiqv">Try adjusting your filters.</p>
                <button onClick={resetFilters} className={`mt-4 font-medium ${rt.accentLink}`} data-id="element-12206" data-mp="2z0uastsibyka">
                  Clear filters
                </button>
              </div>}

            {/* Bottom CTA */}
            <div className={`rounded-xl p-8 text-center border ${dk.border} ${rt.bgLight}`} data-id="element-12207" data-mp="2q1h0jywk75yi">
              <BookOpenIcon className={`h-12 w-12 mx-auto mb-4 ${rt.textPrimary}`} data-id="element-12208" data-mp="2b59gqzp2c6jr" />
              <h2 className={`text-2xl font-bold mb-2 ${dk.textHeading}`} data-id="element-12209" data-mp="9brzd4b4rrbt">
                Never Miss a Release
              </h2>
              <p className={`max-w-lg mx-auto mb-6 ${dk.textSecondary}`} data-id="element-12210" data-mp="e9k3cwp1v3wd">
                Track your favorite authors and get notified when their new
                books drop.
              </p>
              <Link to="/new-release-tracker" className={`inline-flex items-center px-6 py-3 rounded-lg font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5 ${rt.bgDark} hover:opacity-90`} data-id="element-12211" data-mp="26d7lt8xiy4wh">
                Set Up Release Alerts
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>;
}